import React from "react";
import { Column, Heading, If, makeObjectFromArrayMap, Row, TruncatedText } from "@hex-insights/core";
import {
	FormSubmitFunction,
	submissionFailure,
	submissionSuccess,
	SubmitButton,
	useFormState,
	useInternalField,
	useOnSuccess,
} from "@hex-insights/forms";
import { useActivePageRegistration, useHistory } from "@hex-insights/router";
import {
	ButtonLink,
	Contract,
	ContractLineItemComponents,
	ContractUtilities,
	Icon,
	ReEnrollmentPaymentPlanContractListQuery,
	StudentComponents,
	StudentFormat,
	useSetPaymentPlanOnContractMutation,
} from "@hex-insights/verita-romania.shared";
import { ContractLockedNotice, ReEnrollmentLayout } from "../../Components";
import { reEnrollmentReviewPageInfo } from "../../ReviewPage/pageinfo";
import { NumPaymentsField } from "../Components";
import { contractPaymentOptions } from "../payment-plans";
import { reEnrollmentPaymentPlansIndividualPageInfo } from "./pageinfo";
import generalStyles from "../../general.module.css";
import styles from "../form-pages.module.css";

type IndividualFormValues = {
	paymentPlans: PaymentPlanFormValues[];
};

type PaymentPlanFormValues = {
	contractID: Contract["id"];
	numPayments: number;
};

export type ReEnrollmentPaymentPlansIndividualPage = {
	data: ReEnrollmentPaymentPlanContractListQuery;
	backTo: string;
};

export function ReEnrollmentPaymentPlansIndividualPage({ data, backTo }: ReEnrollmentPaymentPlansIndividualPage) {
	useActivePageRegistration(reEnrollmentPaymentPlansIndividualPageInfo);

	const contractMap = React.useMemo(() => {
		return makeObjectFromArrayMap(data.contractConnection.edges, (e) => [e.node.id, e.node]);
	}, [data.contractConnection.edges]);

	const formState = useFormState<IndividualFormValues>({
		initialFormValues: () => ({
			paymentPlans: data.contractConnection.edges.map(({ node: contract }) => ({
				contractID: contract.id,
				numPayments: contractPaymentOptions[contract.contractInstallments.length]
					? contract.contractInstallments.length
					: 1,
			})),
		}),
	});
	useInternalField(formState, "paymentPlans");

	const [setPaymentPlanOnContract] = useSetPaymentPlanOnContractMutation();
	const onSubmit = React.useCallback<FormSubmitFunction<IndividualFormValues>>(
		async ({ formValues }) => {
			if (!formValues.paymentPlans.every((e) => e.numPayments > 0)) {
				return submissionFailure({ _: ["Please select a payment plan for each student."] });
			}

			const promises = [];
			for (let i = 0; i < formValues.paymentPlans.length; i++) {
				const { contractID, numPayments } = formValues.paymentPlans[i];
				promises.push(setPaymentPlanOnContract({ variables: { contractID, numPayments } }));
			}

			const results = await Promise.all(promises);
			for (let i = 0; i < results.length; i++) {
				const result = results[i];
				if (result.errors) {
					return submissionFailure([{ _: ["An error occurred while saving your choices. Please try again."] }]);
				}
			}

			return submissionSuccess();
		},
		[setPaymentPlanOnContract],
	);

	const history = useHistory();
	useOnSuccess(formState, () => history.push(reEnrollmentReviewPageInfo.to));

	const {
		formSetFunctions: { paymentPlans: setPaymentPlans },
	} = formState;
	const setNumPayments = React.useCallback(
		(contractID: Contract["id"], numPayments: number) => {
			setPaymentPlans((prev) => prev.map((e) => (e.contractID === contractID ? { ...e, numPayments } : e)));
		},
		[setPaymentPlans],
	);

	return (
		<ReEnrollmentLayout
			footerContent={
				<Row justify="space-between" align="center">
					<ButtonLink variant="link" to={backTo}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.ArrowLeft size={24} /> Back
						</Row>
					</ButtonLink>

					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Continue
					</SubmitButton>
				</Row>
			}
		>
			<div className={styles["main-content"]}>
				<Heading.H1 className={generalStyles["heading"]}>Please select payment plans</Heading.H1>

				<Column justify="spaced-start" verticalSpacing="3rem">
					{formState.formValues.paymentPlans.map((e) => (
						<IndividualPaymentPlan
							key={e.contractID}
							contract={contractMap[e.contractID]}
							contractID={e.contractID}
							numPayments={e.numPayments}
							setNumPayments={setNumPayments}
						/>
					))}
				</Column>
			</div>
		</ReEnrollmentLayout>
	);
}

type IndividualPaymentPlanProps = {
	contract: ReEnrollmentPaymentPlanContractListQuery["contractConnection"]["edges"][0]["node"];
	contractID: Contract["id"];
	numPayments: number;
	setNumPayments: (contractID: Contract["id"], numPayments: number) => void;
};

function IndividualPaymentPlan({ contract, contractID, numPayments, setNumPayments }: IndividualPaymentPlanProps) {
	const totalPrice = React.useMemo(() => {
		return ContractUtilities.calculateTotalPrice(contract);
	}, [contract]);

	const changesAllowed = ContractUtilities.allowsParentChanges(contract.status);

	return (
		<Column justify="spaced-start" verticalSpacing="0.5rem" className={styles["individual-payment-plan"]}>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
				<StudentComponents.StudentIcon student={contract.student} size={32} />
				<TruncatedText className={styles["individual-payment-plan__student-name"]}>
					{StudentFormat.name(contract.student)}
				</TruncatedText>
			</Row>

			<ContractLineItemComponents.ContractLineItemsTable
				variant="parent"
				contract={contract}
				contractLineItems={contract.contractLineItems}
				discountLabel="Deposit"
				className={styles["individual-payment-plan__line-items-table"]}
			/>

			<Heading.H3 className={generalStyles["heading"]} style={{ margin: "0 0 1rem" }}>
				How would you like to pay?
			</Heading.H3>

			<If condition={!changesAllowed}>
				<ContractLockedNotice style={{ marginBottom: "1rem" }} />
			</If>

			<NumPaymentsField
				numPayments={numPayments}
				setNumPayments={(np: number) => setNumPayments(contractID, np)}
				totalPrice={totalPrice}
				disabled={!changesAllowed}
			/>
		</Column>
	);
}
